import { BabyPeekReveal } from "../../../recoil/types";
import { DeviceType } from "../../../utils/hooks/useDeviceHook";
import { BASE_URL } from "../../../utils/requestUtils";

const BABYPEEK_TRAITS_URL = `${BASE_URL}/api/v1/babypeek_traits`;

export interface SavedCardType {
  payment_method_id: string;
  last_four_digits: string;
  card_brand: string;
  wallet_type: string;
  is_used_for_payment_plan: boolean;
  is_chargeable: boolean;
}

export type Trait =
  | "Sweet"
  | "SweetSalty"
  | "Bitter"
  | "Cilantro"
  | "Freckle"
  | "BabyTeeth"
  | "Asparagus"
  | "EyeColor"
  | "HairThickness"
  | "CurlyHair"
  | "RedHair"
  | "LightOrDarkHair";

export type TraitLabel =
  | "Eye Color"
  | "Light or Dark Hair"
  | "Hair Thickness"
  | "Hair Texture"
  | "Red Hair"
  | "Freckles"
  | "Teething Timeline"
  | "Cilantro Aversion"
  | "Asparagus"
  | "Bitter Taste"
  | "Sweet Taste"
  | "Sweet vs. Salty";

export type LowerCasePageIndex =
  | "sweet"
  | "sweetsalty"
  | "bitter"
  | "cilantro"
  | "freckle"
  | "babyteeth"
  | "asparagus"
  | "eyecolor"
  | "hairthickness"
  | "curlyhair"
  | "redhair"
  | "lightordarkhair"
  | "checkout"
  | "intro"
  | "socialpromo"
  | "summary"
  | "samplereport"
  | "games"
  | "advocacy";

export type BabyPeekButtonNames =
  | "IntroSample"
  | "IntroMainUnlock"
  | "IntroStickyUnlock"
  | "IntroStickySample"
  | "PromoUnlock"
  | "IntroDNAUnlock"
  | "SampleReportUnlock"
  | "IntroPromoUnlock"
  | "PromoJuly4";

export interface BabyPeekTraitResponse {
  requisition: string;
  red_hair_prob: number;
  black_hair_prob: number;
  dark_brown_hair_prob: number;
  brown_hair_prob: number;
  dark_blonde_hair_prob: number;
  blonde_hair_prob: number;
  brown_eyes_prob: number;
  inter_eyes_prob: number;
  blue_eyes_prob: number;
  bitter_taste_prob: number;
  texture_prob: number;
  asparagus_detection_prob: number;
  freckles_prob: number;
  salty_sweet_prob: number;
  aversion_to_cilantro_prob: number;
  hair_thickness_dosage: number;
  time_first_tooth_dosage: number;
  sugar_sensitivity_dosage: number;
  umami_sensitivity_dosage: number;
  reveal_status: BabyPeekReveal;
  reveal_free_trait: boolean;
  bill_details: {
    balance: number;
    portal_bill_status:
      | "not_available"
      | "outstanding"
      | "paid_in_full"
      | "failure"
      | "in_progress"
      | "refunded";
    client_secret?: string;
    start_of_payment_amount: number;
    pk: string;
    is_payment_plan_enabled: boolean;
    show_processing_fee_offer: boolean;
    signup_code: string;
    promo_code: {
      code?: string;
      amount_discounted?: number;
      starting_balance?: number;
      is_enabled?: boolean;
    };
  };
  experiment_details: {
    experiment_id: number;
    landing_page: "a" | "b";
    initial_notification_time: string;
    email_and_sms_pairing: string;
    second_notification_offset: string;
    trait_order: "a" | "b";
    scroll_type: "a" | "b";
    offer_discount: boolean;
    mobile_checkout_type: "a" | "b";
  };
  unlock_type: "payment" | "terms_only" | "advocacy" | "discount_payment";
  first_name: string;
}

export interface BabyPeekFeedbackResponse {
  trait_feedback: {
    [key in Trait]: boolean;
  };
  has_general_feedback: boolean;
}

const getHeaders = (auth_token?: string) => {
  if (auth_token) {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${auth_token}`,
    } as HeadersInit | undefined;
  }
  return {
    "Content-Type": "application/json",
  } as HeadersInit | undefined;
};

const getAuthenticatedHeaders = (auth_token?: string) => {
  if (!auth_token) {
    throw new Error("BabyPeek authentication token is required");
  }
  return getHeaders(auth_token);
};

export const GetBabyPeekDataRequest = async (
  babypeek_pk: string,
  auth_token?: string,
): Promise<Response> =>
  fetch(`${BABYPEEK_TRAITS_URL}/${babypeek_pk}/`, {
    method: "GET",
    headers: getAuthenticatedHeaders(auth_token),
  });

type BabyPeekActionOptions = {
  authToken?: string;
  isFromEmail?: boolean;
  isFromSms?: boolean;
  metaData?: { [key: string]: string | undefined };
};

export const PostBabyPeekAction = async (
  babypeek_pk: string,
  action_type: string,
  device_type: DeviceType,
  { authToken, isFromEmail, isFromSms, metaData }: BabyPeekActionOptions,
): Promise<Response> =>
  fetch(`${BABYPEEK_TRAITS_URL}/${babypeek_pk}/actions/`, {
    body: JSON.stringify({
      action_type,
      device_type,
      is_from_email: isFromEmail,
      is_from_sms: isFromSms,
      meta_data: metaData,
    }),
    method: "POST",
    headers: getAuthenticatedHeaders(authToken),
  });

export const convertPercentage = (percentage: number): number => {
  if (percentage < 0.01) return 0.99;
  if (percentage > 0.99) return 99.1;
  return Math.round(Number((percentage * 100).toFixed(2)));
};

export const GetBabyPeekFeedback = async (
  babypeek_pk: string,
  auth_token?: string,
): Promise<Response> =>
  fetch(`${BABYPEEK_TRAITS_URL}/${babypeek_pk}/feedback/`, {
    method: "GET",
    headers: getHeaders(auth_token),
  });

export const PostBabyPeekUpdateGiftEmail = async (
  babypeek_pk: string,
  client_secret: string,
  receipt_email: string,
  auth_token?: string,
): Promise<Response> =>
  fetch(`${BABYPEEK_TRAITS_URL}/${babypeek_pk}/update_gifting_receipt_email/`, {
    body: JSON.stringify({
      client_secret,
      receipt_email,
    }),
    method: "POST",
    headers: getAuthenticatedHeaders(auth_token),
  });

export const PostGeneralBabyPeekFeedback = async (
  babypeek_pk: string,
  interest_level: number,
  recommend_level: number,
  custom_feedback: string,
  agree_to_be_contacted: boolean,
  auth_token?: string,
): Promise<Response> =>
  fetch(`${BABYPEEK_TRAITS_URL}/${babypeek_pk}/submit_general_feedback/`, {
    body: JSON.stringify({
      interest_level,
      recommend_level,
      custom_feedback,
      agree_to_be_contacted,
    }),
    method: "POST",
    headers: getHeaders(auth_token),
  });

export const PostTraitBabyPeekOneTime = async (options: {
  authToken?: string;
  babypeekPK: string;
  email?: string;
  isSaved: boolean;
  paymentIntentID: string;
  paymentMethodID?: string;
}): Promise<Response> =>
  fetch(
    `${BABYPEEK_TRAITS_URL}/${options.babypeekPK}/submit_one_time_payment/`,
    {
      method: "POST",
      body: JSON.stringify({
        email: options.email,
        is_saved: options.isSaved,
        payment_intent_id: options.paymentIntentID,
        payment_method_id: options.paymentMethodID,
      }),
      headers: getAuthenticatedHeaders(options.authToken),
    },
  );

export const GetBabyPeekPaymentPlan = async (
  babypeek_pk: string,
  auth_token?: string,
): Promise<Response> =>
  fetch(`${BABYPEEK_TRAITS_URL}/${babypeek_pk}/get_payment_plan/`, {
    method: "GET",
    headers: getAuthenticatedHeaders(auth_token),
  });

export const GetSavedCardsBabyPeek = async (
  babypeek_pk: string,
  access_token: string,
): Promise<Response> =>
  fetch(`${BASE_URL}/api/v1/traits/${babypeek_pk}/payment_methods/`, {
    method: "GET",
    headers: getAuthenticatedHeaders(access_token),
  });

export const PayWithSavedCardBabyPeek = async (
  babypeek_pk: string,
  payment_method_id: string,
  email: string,
  payment_type: "pay_in_full" | "payment_plan",
  access_token: string,
): Promise<Response> =>
  fetch(`${BASE_URL}/api/v1/traits/${babypeek_pk}/submit_saved_payment/`, {
    method: "POST",
    body: JSON.stringify({ payment_method_id, email, payment_type }),
    headers: getAuthenticatedHeaders(access_token),
  });

export const ShareBabyPeekPDFPayment = async (
  babypeek_pk: string,
  custom_message: string,
  email: string,
  language_code: "es" | "en",
  auth_token?: string,
): Promise<Response> =>
  fetch(`${BABYPEEK_TRAITS_URL}/${babypeek_pk}/generate_pdf/`, {
    method: "POST",
    body: JSON.stringify({ custom_message, language_code, email }),
    headers: getAuthenticatedHeaders(auth_token),
  });

export const getUnlockButtonText = (): string => "Unlock BabyPeek";

export const BabyPeekDownloadGames = async (
  babypeek_pk: string,
  game_type: "GuessTheLittleOne" | "PeekABoo" | "TraitsReveal",
  download_type: "AnswerSheet" | "GamePlay" | "Preview",
  auth_token?: string,
): Promise<Response> =>
  fetch(`${BASE_URL}/api/v1/traits/${babypeek_pk}/download_game/`, {
    method: "POST",
    headers: getAuthenticatedHeaders(auth_token),
    body: JSON.stringify({ game_type, download_type }),
  });

export const shouldShowBabyPeekNav = (reveal_status: BabyPeekReveal) =>
  reveal_status === "pending" || reveal_status === "available";
